/**
 * ConsentBanner — мягкий GDPR баннер внизу экрана при первом заходе.
 *
 * JB task #51 / doc 15 §analytics. Появляется один раз когда
 * `useConsentStore.analytics === 'unknown'`. После явного выбора
 * (grant/deny) больше не появляется.
 *
 * Текст в языке присутствия — Сай предлагает поделиться, не пугает
 * юридическим жаргоном. Принцип #6 (без тёмных паттернов): обе кнопки
 * равноправны по визуальному весу.
 *
 * Закрытие выбора не блокирует игру — баннер только информирует.
 * Игрок может тапать Искру параллельно, баннер сидит снизу как тонкая
 * полоска. Закрывается мягким slide-down при выборе.
 */
import { AnimatePresence, motion } from 'framer-motion';
import { useArtifactsStore } from '../visual-artifacts/store.js';
import { useGameStore } from '../stores/game.js';
import { useProfileAskStore } from '../stores/profileAsk.js';
import { useConsentStore } from '../telemetry/consent.js';
import styles from './ConsentBanner.module.css';

/** Должен совпадать с MIN_TAPS_FOR_ASK в NameSoftAsk.tsx. */
const NAME_ASK_TAPS_THRESHOLD = 8;

export function ConsentBanner(): JSX.Element {
  const status = useConsentStore((s) => s.analytics);
  const grant = useConsentStore((s) => s.grant);
  const deny = useConsentStore((s) => s.deny);

  // JB 2026-05-25 production data: ConsentBanner + NameSoftAsk + Sai
  // activation накладываются — ConsentBanner просвечивает сквозь
  // полупрозрачный backdrop NameSoftAsk (alpha 0.55). Прячем banner
  // пока более «весомые» overlay'и активны или вот-вот откроются;
  // после dismiss/save они снова видны.
  const activeActivation = useGameStore((s) => s.activeActivation);
  const revealQueue = useArtifactsStore((s) => s.revealQueue);
  // NameSoftAsk показывается когда totalTaps >= 8 + displayName === null
  // + nameAsked === false. Approximation тех же условий — если все true,
  // ask или уже открыт или сейчас откроется (там 1.2s delay).
  const totalTaps = useGameStore((s) => s.state.stats.totalTaps);
  const displayName = useGameStore((s) => s.state.profile.displayName);
  const nameAsked = useProfileAskStore((s) => s.nameAsked);
  const nameAskPending = totalTaps >= NAME_ASK_TAPS_THRESHOLD && displayName === null && !nameAsked;

  const obstructed = activeActivation !== null || revealQueue.length > 0 || nameAskPending;

  return (
    <AnimatePresence>
      {status === 'unknown' && !obstructed && (
        <motion.div
          className={styles.banner}
          role="dialog"
          aria-label="Согласие на аналитику"
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.25, 0.8, 0.3, 1] }}
        >
          <p className={styles.text}>
            Сай ведёт лёгкий счёт твоих шагов чтобы делать игру лучше.
            <br />
            <span className={styles.dim}>
              без личных данных. согласие можно изменить в настройках.
            </span>
          </p>
          <div className={styles.actions}>
            <button type="button" className={styles.deny} onClick={deny}>
              лучше нет
            </button>
            <button type="button" className={styles.grant} onClick={grant}>
              хорошо
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
